import React, { useCallback, useEffect, useRef, useState } from 'react';
import { message, Modal } from 'antd';

import { ExclamationCircleOutlined } from '@ant-design/icons';

import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';

import { useDispatch, useSelector, useStore } from 'react-redux';

import { isEmpty as _isEmpty } from 'lodash-es';

import { editFormSettings } from '../../store/actions/formActions';

import FormSettingsMenu from './FormSettingsMenu';
import GeneralSettings from './GeneralSettings';
import NotificationSettings from './NotificationSettings';
import SeoSettings from './SeoSettings';
import ResponsesSettings from './ResponseSettings';

import { formByIdSelector } from '../../store/selectors/formSelector';
import { CountryCodeContext, formSettingsSchema } from './helpers';
import useCountryCode from '../../hooks/useCountryCode';
import 'react-intl-tel-input/dist/main.css';
import './style.less';
import IntegrationSettings from './IntegrationSettings';
import { useParams } from 'react-router-dom';

const { confirm } = Modal;

const FormSettings = () => {
  const params = useParams();
  const store = useStore();
  const { me } = useSelector((state) => state.auth);

  const [screen, setScreen] = useState(
    Cookies.get('redirected-from-gsheet-auth') ? 'integrations' : 'general',
  );
  const [isLoading, setLoading] = useState(false);
  const countryCode = useCountryCode();

  const activeForm = formByIdSelector(store.getState(), params.id);

  useEffect(() => {
    if (Cookies.get('redirected-from-gsheet-auth')) {
      Cookies.remove('redirected-from-gsheet-auth');
    }
  }, []);

  const dirtyFieldsRef = useRef();

  function handleMenuChange(key) {
    if (_isEmpty(dirtyFieldsRef.current)) {
      setScreen(key);
    } else {
      confirm({
        title: t('formSettings.discardChanges'),
        icon: <ExclamationCircleOutlined />,
        content: <div>{t('formSettings.discardChangesMessage')}</div>,
        okText: t('formSettings.yes'),
        okType: 'danger',
        cancelText: t('formSettings.no'),
        centered: true,
        onOk() {
          dirtyFieldsRef.current = null;
          setScreen(key);
        },
      });
    }
  }

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const handleDispatch = useCallback(
    async (params) => {
      if (_isEmpty(activeForm)) return;
      if (
        params.responseJumpType !== 'default' &&
        params?.responseJumpData &&
        params.responseJumpData.length === 0
      ) {
        message.error('Please provide at least one phone number.');
        return;
      }

      try {
        const formSettings = await formSettingsSchema.validate(
          {
            name: 'name' in params ? params.name : activeForm.name,
            link: 'link' in params ? params.link : activeForm.link,
            language: 'language' in params ? params.language : activeForm.language,
            enabled: 'enabled' in params ? params.enabled : activeForm.enabled,
            whatsappNumber:
              'whatsappNumber' in params ? params.whatsappNumber : activeForm.whatsappNumber,
            submitText: 'submitText' in params ? params.submitText : activeForm.submitText,
            meta: 'meta' in params ? params.meta : activeForm.meta,
            responseJumpType:
              'responseJumpType' in params ? params.responseJumpType : activeForm.responseJumpType,
            responseJumpData:
              'responseJumpData' in params ? params.responseJumpData : activeForm.responseJumpData,
            customDomain: 'customDomain' in params ? params.customDomain : activeForm.customDomain,
            pushNotificationEnabled:
              'pushNotificationEnabled' in params
                ? params.pushNotificationEnabled
                : activeForm.pushNotificationEnabled ?? true,
            emailNotificationEnabled:
              'emailNotificationEnabled' in params
                ? params.emailNotificationEnabled
                : activeForm.emailNotificationEnabled ?? false,
            emailNotificationEmail:
              'emailNotificationEmail' in params
                ? params.emailNotificationEmail
                : activeForm.emailNotificationEmail || '',
          },
          { stripUnknown: true, recursive: true },
        );

        setLoading(true);
        dispatch(editFormSettings(activeForm.id, formSettings)).then((event) => {
          if (event && event.type === 'EDIT_FORM_FAIL') {
            message.error(event.payload.error);
          } else if (event) {
            message.success(t('formSettings.updateSuccess'));
          }
          setLoading(false);
        });
      } catch (error) {
        message.error(error.message);
        setLoading(false);
      }
    },
    [activeForm, dispatch, t],
  );

  const settingsScreens = {
    general: (
      <GeneralSettings
        plan={me.plan || 'free'}
        customDomain={me.customDomain}
        isLoading={isLoading}
        form={activeForm}
        handleDispatch={handleDispatch}
        dirtyFieldsRef={dirtyFieldsRef}
      />
    ),
    notifications: (
      <NotificationSettings
        plan={me.plan || 'free'}
        isLoading={isLoading}
        form={activeForm}
        handleDispatch={handleDispatch}
        dirtyFieldsRef={dirtyFieldsRef}
      />
    ),
    seo: (
      <SeoSettings
        plan={me.plan || 'free'}
        isLoading={isLoading}
        form={activeForm}
        handleDispatch={handleDispatch}
        dirtyFieldsRef={dirtyFieldsRef}
      />
    ),
    team: (
      <ResponsesSettings
        plan={me.plan || 'free'}
        isLoading={isLoading}
        form={activeForm}
        handleDispatch={handleDispatch}
        dirtyFieldsRef={dirtyFieldsRef}
      />
    ),
    integrations: <IntegrationSettings form={activeForm} user={me} plan={me.plan || 'free'} />,
  };

  return (
    <div className="settings-page-layout">
      <FormSettingsMenu
        isLoading={isLoading}
        handleMenuChange={handleMenuChange}
        form={activeForm}
        screen={screen}
      />
      <CountryCodeContext.Provider value={countryCode}>
        <div className="form-settings-screen-container">{settingsScreens[screen]}</div>
      </CountryCodeContext.Provider>
    </div>
  );
};

export default FormSettings;
